'use client';

import Autoplay from 'embla-carousel-autoplay';
import Fade from 'embla-carousel-fade';
import type { StaticImageData } from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/icon';
import { Carousel, type CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { CastCard } from './cast-card';
interface CastMember {
  id: string;
  name: string;
  role: string;
  image: StaticImageData;
}
interface CastSliderProps {
  castData: CastMember[];
}
export function CastSlider({
  castData
}: CastSliderProps) {
  const [api, setApi] = useState<CarouselApi>();
  const autoplayPlugin = useRef(Autoplay({
    delay: 2000,
    stopOnInteraction: false,
    stopOnMouseEnter: false
  }));
  const fadePlugin = useRef(Fade());
  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);
  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);
  useEffect(() => {
    if (!api) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [api, scrollPrev, scrollNext]);
  return <div className='relative w-full overflow-hidden'>
      <Carousel setApi={setApi} plugins={[fadePlugin.current, autoplayPlugin.current]} opts={{
      loop: true,
      duration: 20
    }} className='relative w-full'>
        <CarouselContent className='ml-0'>
          {castData.map(cast => <CarouselItem key={cast.id} className='pl-0'>
              <CastCard id={cast.id} name={cast.name} role={cast.role} image={cast.image} />
            </CarouselItem>)}
        </CarouselContent>

        <button type='button' onClick={scrollPrev} className='absolute top-1/2 left-6 z-20 -translate-y-1/2 p-2 transition-opacity hover:opacity-70 sm:left-12' aria-label='Previous slide'>
          <Icon name='chevron' className='h-6 w-6 rotate-180 text-white' />
        </button>
        <button type='button' onClick={scrollNext} className='absolute top-1/2 right-6 z-20 -translate-y-1/2 p-2 transition-opacity hover:opacity-70 sm:right-12' aria-label='Next slide'>
          <Icon name='chevron' className='h-6 w-6 text-white' />
        </button>
      </Carousel>

      <div className='absolute top-1/2 left-1/2 z-30 w-full max-w-[25ch] -translate-x-1/2 -translate-y-1/2 text-center text-white sm:max-w-xs'>
        <Icon name='lock' className='mx-auto mb-2 size-5 fill-black/30 text-white' />
        <p className='mb-3 font-bold text-[1.1rem] tracking-[0.04em]'>
          Pay It Forward to access extended interviews
        </p>
        <Link href='/pay-it-forward' className='inline-block bg-white px-5 py-2.5 font-bold text-[0.9rem] text-black uppercase tracking-[0.06em] transition-opacity hover:opacity-90 sm:px-6 sm:py-3'>
          Pay it Forward
        </Link>
      </div>
    </div>;
}